/* jp-theme.js — small global enhancements (no jQuery, no frameworks).
   1) Mirrors Dawn's #cart-icon-bubble count into [data-jp-cart-count]
      (mobile header + bottom nav) via MutationObserver — survives any
      Dawn AJAX cart update without touching Dawn internals.
   2) Injects the cart free-delivery progress bar; ports Dawn's StickyHeader.
   (Drawer + search now open via [data-jp-open] — handled by jp-layers.js.) */
(() => {
  if (window.__jpThemeInit) return;
  window.__jpThemeInit = true;
  const syncCount = () => {
    const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden]');
    const count = bubble ? bubble.textContent.trim() : '';
    document.querySelectorAll('[data-jp-cart-mirror]').forEach((wrap) => {
      let b = wrap.querySelector('.cart-count-bubble');
      if (count) {
        if (!b) {
          b = document.createElement('span');
          b.className = 'cart-count-bubble';
          b.innerHTML = '<span aria-hidden="true" data-jp-cart-count></span>';
          wrap.appendChild(b);
        }
        b.querySelector('[data-jp-cart-count]').textContent = count;
      } else if (b) {
        b.remove();
      }
    });
  };

  /* ---- trust-bar announcement rotation (2–3 messages, slow crossfade) ----
     Static with one message; static under prefers-reduced-motion; pauses on
     hover/focus so links stay clickable. */
  const initAnnounce = () => {
    document.querySelectorAll('[data-jp-announce]').forEach((bar) => {
      if (bar.dataset.jpAnnounceReady) return;
      bar.dataset.jpAnnounceReady = 'true';
      const items = [...bar.querySelectorAll('[data-jp-announce-item]')];
      if (items.length < 2) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const interval = parseInt(bar.dataset.interval, 10) || 8000;
      let i = 0;
      let timer = null;
      const show = (n) => {
        items.forEach((el, idx) => el.classList.toggle('is-active', idx === n));
      };
      const start = () => {
        if (timer) return;
        timer = window.setInterval(() => {
          i = (i + 1) % items.length;
          show(i);
        }, interval);
      };
      const stop = () => { window.clearInterval(timer); timer = null; };
      bar.addEventListener('mouseenter', stop);
      bar.addEventListener('mouseleave', start);
      bar.addEventListener('focusin', stop);
      bar.addEventListener('focusout', start);
      start();
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    initAnnounce();
    document.addEventListener('shopify:section:load', () => initAnnounce());

    const src = document.getElementById('cart-icon-bubble');
    if (src) new MutationObserver(syncCount).observe(src, { childList: true, subtree: true });
    syncCount();

    /* persona grid + drawer now live in snippets/jp-drawer.liquid (server-rendered) */

    /* ---- Phase 6a · free-delivery progress bar in the cart drawer ---- */
    const shipTpl = document.querySelector('template[data-jp-shipbar-template]');
    if (shipTpl) {
      const threshold = parseInt(shipTpl.dataset.thresholdCents, 10) || 0;
      const currency = shipTpl.dataset.currency || 'GBP';
      const lang = document.documentElement.lang || 'en';
      let lastTotal = parseInt(shipTpl.dataset.totalCents, 10) || 0;
      const fmt = (cents) => {
        try {
          return new Intl.NumberFormat(lang, { style: 'currency', currency, minimumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);
        } catch { return (cents / 100).toFixed(2); }
      };
      const renderBar = (total) => {
        lastTotal = total;
        const drawer = document.querySelector('cart-drawer .drawer__inner');
        if (!drawer || threshold <= 0) return;
        if (drawer.querySelector('.drawer__inner-empty') || total <= 0) {
          drawer.querySelector('.jp-shipbar')?.remove();
          return;
        }
        let bar = drawer.querySelector('.jp-shipbar');
        if (!bar) {
          const frag = shipTpl.content.cloneNode(true);
          const header = drawer.querySelector('.drawer__header');
          if (header) header.after(frag);
          else drawer.prepend(frag);
          bar = drawer.querySelector('.jp-shipbar');
        }
        if (!bar) return;
        const reached = total >= threshold;
        bar.classList.toggle('is-reached', reached);
        bar.querySelector('.jp-shipbar__msg').innerHTML = reached
          ? shipTpl.dataset.msgReached
          : shipTpl.dataset.msgAway.replace('[amount]', '<b>' + fmt(threshold - total) + '</b>');
        bar.querySelector('.jp-shipbar__fill').style.width = Math.min(100, (total / threshold) * 100) + '%';
      };
      let shipTimer;
      const refreshBar = () => {
        window.clearTimeout(shipTimer);
        shipTimer = setTimeout(() => {
          fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)
            .then((r) => r.json())
            .then((c) => renderBar(c.total_price))
            .catch(() => renderBar(lastTotal));
        }, 150);
      };
      renderBar(lastTotal);
      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, refreshBar);
      }
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) new MutationObserver(refreshBar).observe(bubble, { childList: true, subtree: true });
    }

    /* drawer + search open via [data-jp-open] — handled by jp-layers.js */
  });
})();

/* ============================================================
   E2 · StickyHeader — ported verbatim from Dawn 15.4.1
   sections/header.liquid {% javascript %} (L316-426), because we
   render jp-header instead of Dawn's header section. Contract:
   targets .section-header wrapper; classes styled by base.css.
   ============================================================ */
if (!customElements.get('sticky-header')) {
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.header = document.querySelector('.section-header');
      if (!this.header) return;
      this.headerIsAlwaysSticky =
        this.getAttribute('data-sticky-type') === 'always' ||
        this.getAttribute('data-sticky-type') === 'reduce-logo-size';
      this.headerBounds = {};
      this.setHeaderHeight();
      window.matchMedia('(max-width: 990px)').addEventListener('change', this.setHeaderHeight.bind(this));
      if (this.headerIsAlwaysSticky) this.header.classList.add('shopify-section-header-sticky');
      this.currentScrollTop = 0;
      this.preventReveal = false;
      this.predictiveSearch = this.querySelector('predictive-search');
      this.onScrollHandler = this.onScroll.bind(this);
      this.hideHeaderOnScrollUp = () => (this.preventReveal = true);
      this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.addEventListener('scroll', this.onScrollHandler, { passive: true });
      this.createObserver();
    }
    setHeaderHeight() {
      document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
    }
    disconnectedCallback() {
      this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.removeEventListener('scroll', this.onScrollHandler);
    }
    createObserver() {
      const observer = new IntersectionObserver((entries, obs) => {
        this.headerBounds = entries[0].intersectionRect;
        obs.disconnect();
      });
      observer.observe(this.header);
    }
    onScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      if (this.predictiveSearch && this.predictiveSearch.isOpen) return;
      if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        if (this.preventHide) return;
        requestAnimationFrame(this.hide.bind(this));
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        if (!this.preventReveal) {
          requestAnimationFrame(this.reveal.bind(this));
        } else {
          window.clearTimeout(this.isScrolling);
          this.isScrolling = setTimeout(() => { this.preventReveal = false; }, 66);
          requestAnimationFrame(this.hide.bind(this));
        }
      } else if (scrollTop <= this.headerBounds.top) {
        this.header.classList.remove('scrolled-past-header');
        requestAnimationFrame(this.reset.bind(this));
      }
      this.currentScrollTop = scrollTop;
    }
    hide() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      this.closeSearchModal();
    }
    reveal() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-sticky', 'animate');
      this.header.classList.remove('shopify-section-header-hidden');
    }
    reset() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.remove('shopify-section-header-hidden', 'shopify-section-header-sticky', 'animate');
    }
    closeSearchModal() {
      this.searchModal = this.searchModal || this.header.querySelector('details-modal');
      if (this.searchModal && typeof this.searchModal.close === 'function') this.searchModal.close(false);
    }
  }
  customElements.define('sticky-header', StickyHeader);
}

/* ============================================================
   E3 · Analytics — GA4-ready dataLayer events (no-op without GTM)
   ============================================================ */
(() => {
  if (window.__jpAnalyticsInit) return;
  window.__jpAnalyticsInit = true;
  const push = (event, data) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event }, data));
  };
  document.addEventListener('persona:changed', (e) => push('jp_persona_changed', { persona: e.detail.persona }));
  document.addEventListener('click', (e) => {
    const pill = e.target.closest('.jp-drawer__pill');
    if (pill) push('jp_drawer_quick_click', { label: pill.textContent.trim() });
    const tab = e.target.closest('[data-jp-ftab]');
    if (tab) push('jp_ftab_switched', { label: tab.textContent.trim() });
  });
  let cpcTimer;
  document.addEventListener('input', (e) => {
    if (!e.target.matches('[data-cpc-cuts], [data-cpc-years]')) return;
    const root = e.target.closest('[data-jp-cpc]');
    window.clearTimeout(cpcTimer);
    cpcTimer = setTimeout(() => {
      push('jp_cpc_used', {
        cuts: root?.querySelector('[data-cpc-cuts]')?.value,
        years: root?.querySelector('[data-cpc-years]')?.value,
      });
    }, 900);
  });
})();
